import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import {
  Button,
  EmptyIndicator,
  LoadingIndicator,
  ModalButtonWithForm
} from 'components/common';
import { Select } from 'components/common/form';
import { CoursePicture } from 'components/courses/common';
import { useHomePageSettingsContext } from '.';

import type { GetCoursesMinimalRes } from 'shared-types/responses';
import type { ModuleInSettings } from './HomePageSettingsModules';

type Props = {
  module: ModuleInSettings;
  url: string;
} & (
  | { includeActions?: false; updateModule?: never }
  | { includeActions: true; updateModule: (module: ModuleInSettings) => void }
);

const HomePageSettingsTilesCourses = ({
  includeActions = false,
  module,
  updateModule,
  url
}: Props) => {
  const {
    messages,
    serverConfig: { enableCoursePicture },
    theme
  } = useReduxState();

  const { courseOptions } = useHomePageSettingsContext();

  const { data, loading } = useApiData<GetCoursesMinimalRes>({
    url: url + '&onlyCourseData=true',
    onlyIf: Boolean(url)
  });

  const copyCourses = () => [...module.courses];

  const handleAddCourse = ({ courseId }: { courseId: number }) => {
    if (!courseId) return;

    const modifiedModule = {
      ...module,
      limit: (module.courses?.length || 0) + 1,
      courses: [...(module.courses || []), courseId]
    };

    setTimeout(() => updateModule(modifiedModule));
  };

  const handleMoveCourse = (courseIndex: number, increment: number) => {
    const courses = copyCourses();
    const movedCourse = courses.splice(courseIndex, 1)[0];
    courses.splice(courseIndex + increment, 0, movedCourse);
    updateModule(JSON.parse(JSON.stringify({ ...module, courses })));
  };

  const handleRemoveCourse = (courseIndex: number) => {
    const courses = copyCourses();
    courses.splice(courseIndex, 1);
    updateModule({ ...module, courses, limit: courses.length });
  };

  return (
    <div>
      {Boolean(url) && loading ? (
        <LoadingIndicator />
      ) : data?.courses?.length || includeActions ? (
        <div>
          <div className="courses">
            {data?.courses?.map((course, i: number) => (
              <div className="course" key={course.id}>
                {enableCoursePicture ? (
                  <div className="picture">
                    <CoursePicture
                      position={course.picturePosition}
                      size={480}
                      url={course.picture}
                    />
                  </div>
                ) : null}

                <div className="content">
                  <div className="title">{course.title}</div>

                  {includeActions ? (
                    <div className="content-actions">
                      <Button
                        category="lighter"
                        disabled={i === 0}
                        icon="arrow-left"
                        isRounded={true}
                        onClick={() => handleMoveCourse(i, -1)}
                        size="small"
                        tooltip={messages.commonMove}
                      />
                      <Button
                        category="lighter"
                        disabled={i === module.courses.length - 1}
                        icon="arrow-right"
                        isRounded={true}
                        onClick={() => handleMoveCourse(i, 1)}
                        size="small"
                        tooltip={messages.commonMove}
                      />
                      <Button
                        category="lighter"
                        icon="trash-alt"
                        isRounded={true}
                        onClick={() => handleRemoveCourse(i)}
                        size="small"
                        tooltip={messages.commonRemove}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            ))}

            {includeActions ? (
              <div className="add">
                <ModalButtonWithForm
                  buttonCategory="transparent"
                  buttonIcon="plus"
                  buttonStyle={{
                    alignItems: 'center',
                    border: `2px dashed ${theme.lightGreyDarkActive}`,
                    borderRadius: theme.cardBorderRadius,
                    display: 'flex',
                    height: '100%',
                    justifyContent: 'center',
                    width: '100%'
                  }}
                  buttonText={messages.commonAddCourse}
                  modalActionCategory="success"
                  modalActionText={messages.commonAdd}
                  modalTitle={messages.commonAddCourse}
                  onSubmit={handleAddCourse}
                  preventSubmit={true}
                  pristineIsValid={false}
                >
                  {() => (
                    <Select
                      field="courseId"
                      isSearchable={true}
                      noOptionsMessage={messages.coursesNoCoursesFound}
                      options={courseOptions.filter(
                        (course) =>
                          !(module.courses || []).includes(course.value)
                      )}
                      placeholder={messages.coursesSearch}
                    />
                  )}
                </ModalButtonWithForm>
              </div>
            ) : null}
          </div>

          <div className="disclaimer">
            {`* ${messages.homePageModuleCoursesDisclaimer}`}
          </div>
        </div>
      ) : (
        <EmptyIndicator
          icon="book"
          iconIsDarker={true}
          size="small"
          title={messages.coursesNoCoursesFound}
        />
      )}

      <style jsx>{`
        .courses {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          gap: 20px;
          padding: 20px;
        }
        .add,
        .course {
          background-color: white;
          border-radius: ${theme.cardBorderRadius};
          display: flex;
          flex-direction: column;
          outline: 0;
          transition: box-shadow 0.3s;
          width: 100%;
          min-height: 210px;
          min-width: 100px;
        }
        .add :global(> button) {
          background-color: ${theme.lightGreyLighter};
          color: ${theme.textColorLighter};
        }
        .add :global(> button:hover) {
          background-color: white;
          color: ${theme.textColor};
        }
        .picture {
          border-radius: ${theme.cardBorderRadius} ${theme.cardBorderRadius} 0 0;
          height: 90px;
          flex-shrink: 0;
          overflow: hidden;
          position: relative;
          width: 100%;
        }
        .content {
          display: flex;
          flex-direction: column;
          flex-grow: 1;
          padding: 10px 15px;
        }
        .title {
          color: ${theme.textColor};
          display: -webkit-box;
          font-size: 1.4rem;
          font-weight: 600;
          justify-content: space-between;
          overflow: hidden;
          text-overflow: ellipsis;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 3;
        }
        .content-actions {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
          margin-top: auto;
          padding-top: 10px;
        }
        .disclaimer {
          color: ${theme.textColorLight};
          font-size: 1.4rem;
          font-weight: 600;
          margin: 0 20px 20px;
        }
        @media (min-width: ${breakpoints.small}px) {
          .add,
          .course {
            width: calc(50% - 10px);
          }
        }
        @media (min-width: ${breakpoints.medium}px) {
          .add,
          .course {
            width: calc(33.333% - 13.333px);
          }
        }
        @media (min-width: ${breakpoints.large}px) {
          .add,
          .course {
            width: calc(25% - 15px);
          }
        }
      `}</style>
    </div>
  );
};

export default HomePageSettingsTilesCourses;
